import React from 'react';
import { Link } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Avatar,
  IconButton,
  Skeleton,
  Chip
} from '@mui/material';
import {
  Celebration as FunIcon,
  ArrowForward as ArrowIcon,
  EmojiEvents as TrophyIcon,
  Favorite as HeartIcon,
  SentimentVerySatisfied as SmileIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const safeAge = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return formatDistanceToNow(d, { addSuffix: true });
  } catch { return ''; }
};

/**
 * Fun & Social Widget
 * Shows fun family activities and social content
 */
const FunSocialWidget = ({ funItems = [], loading = false }) => {
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Skeleton variant="text" width={150} height={32} />
        </Box>
        {[1, 2, 3].map((i) => (
          <Box key={i} sx={{ mb: 2 }}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
        ))}
      </Paper>
    );
  }

  const getActivityIcon = (type) => {
    switch (type) {
      case 'achievement':
        return <TrophyIcon sx={{ fontSize: 16 }} />;
      case 'celebration':
        return <HeartIcon sx={{ fontSize: 16 }} />;
      default:
        return <SmileIcon sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ bgcolor: '#8e24aa', mr: 2 }}>
            <FunIcon />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Fun & Social
          </Typography>
        </Box>
        <IconButton
          component={Link}
          to="/fun-social"
          color="primary"
          size="small"
        >
          <ArrowIcon />
        </IconButton>
      </Box>

      {/* Content */}
      {!funItems || funItems.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <FunIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No fun activities yet
          </Typography>
          <Typography
            variant="caption"
            color="primary"
            component={Link}
            to="/fun-social"
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Share something fun
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {funItems.slice(0, 5).map((item, index) => (
            <ListItem
              key={item.id}
              component={Link}
              to="/fun-social"
              sx={{
                px: 0,
                py: 1.5,
                borderBottom: index < Math.min(funItems.length, 5) - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': { bgcolor: 'action.hover' }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                      {item.title}
                    </Typography>
                    {item.type && (
                      <Chip
                        icon={getActivityIcon(item.type)}
                        label={item.type}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize' }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {item.description?.substring(0, 80)}
                      {item.description?.length > 80 && '...'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {item.authorName || item.createdBy} •{' '}
                      {item.createdAt && safeAge(item.createdAt)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default FunSocialWidget;
